import React, { ReactNode, useContext, useState } from 'react';
import cx from 'classnames';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  ImageRelatedContentPropsDto,
  RelatedContentDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import {
  buildEbookCfiSsoUrlRequestFromRelatedImage,
  buildEbookSsoUrlRequestFromRelatedImage,
  openEbookAtCfiOrPageRange,
  getBreadcrumbsDisplayParts,
  getImageChunkProps,
  getIsBookClickable,
  getPageNumberFromBreadcrumbs,
  getRelatedImageIsbn,
  toastApiError
} from '../../pages/ai-chat/ai-chat.utilities';
import { getRelatedContentImageUrl } from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.utilities';
import { AppContext } from '../app/AppProvider.component';
import { trackNewRelicAction } from '../app/app-state-actions.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { isFeatureFlagEnabled } from '../../utilities/featureFlag.utilities';

export type RelatedContentImagePanelPropsOnly = {
  relatedContent: RelatedContentDto;
};

export type RelatedContentImagePanelProps = RelatedContentImagePanelPropsOnly & ELSPropsFromToastService;

export const RelatedContentImagePanelComponent = (props: RelatedContentImagePanelProps): JSX.Element => {

  const {
    appState,
    appStateRef,
    setAppState
  } = useContext(AppContext);

  const [isImageExpanded, setIsImageExpanded] = useState<boolean>(false);

  const handleImageSourceClick = (imageProps: ImageRelatedContentPropsDto) => {
    const chunkProps = getImageChunkProps(imageProps);
    const pageStart = getPageNumberFromBreadcrumbs(chunkProps?.breadCrumbs);

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_RELATED_CONTENT_SOURCE_LINK_CLICK,
        props: {
          relatedContentId: props.relatedContent.id,
          isbn: chunkProps?.ebookData?.isbn,
          page: pageStart,
          contentType: props.relatedContent.contentType,
          entryId: props.relatedContent.entryId,
        }
      }
    });

    const cfiRequest = isFeatureFlagEnabled(
      FEATURE_FLAG.ENABLE_EBOOK_CFI_LINKING,
      appState.featureFlagsGrouped,
      appState.courseSectionId
    )
      ? buildEbookCfiSsoUrlRequestFromRelatedImage(appState, props.relatedContent)
      : null;

    openEbookAtCfiOrPageRange({
      appState,
      appStateRef,
      setAppState,
      cfiRequest,
      buildPageRangeRequest: () => buildEbookSsoUrlRequestFromRelatedImage(appState, props.relatedContent)
    })
      .catch(() => {
        toastApiError(props.toastService);
      });
  };

  const isSourceClickable = (): boolean => {
    const isbn = getRelatedImageIsbn(props.relatedContent);
    return getIsBookClickable(appState, isbn);
  };

  const renderSource = (): ReactNode => {
    const imageProps = props.relatedContent.contentProps as ImageRelatedContentPropsDto;
    const chunkProps = getImageChunkProps(imageProps);
    const sourceDisplay = (<>Source: {chunkProps?.ebookData?.title}</>);

    if (!isSourceClickable()) {
      return sourceDisplay;
    }

    return (
      <button
        type="button"
        className="u-els-debuttonize u-els-anchorize"
        onClick={() => {
          handleImageSourceClick(imageProps);
        }}
      >
        {sourceDisplay}
      </button>
    );
  };

  const imageProps = props.relatedContent.contentProps as ImageRelatedContentPropsDto;
  const chunkProps = getImageChunkProps(imageProps);
  const breadcrumbParts = getBreadcrumbsDisplayParts(chunkProps?.breadCrumbs);

  return (
    <div>
      {
        (breadcrumbParts.chapter || breadcrumbParts.sections) && (
          <h4 className="u-els-margin-bottom">
            {breadcrumbParts.chapter && (
              <div className="u-els-margin-right-1o2">{breadcrumbParts.chapter}</div>
            )}
            {breadcrumbParts.sections}
          </h4>
        )
      }

      <button
        type="button"
        onClick={() => setIsImageExpanded((prev) => !prev)}
        className="u-els-debuttonize"
        style={{ cursor: 'pointer', display: 'block' }}
      >
        <img
          src={getRelatedContentImageUrl(props.relatedContent.id, appState.registeredToken)}
          alt={chunkProps?.description || props.relatedContent.title || 'Related image'}
          className={cx('c-naa-panel__image', {
            'c-naa-panel__image--expanded': isImageExpanded
          })}
        />
      </button>
      {chunkProps?.caption && (
        <div className="u-els-margin-top">
          {chunkProps.caption}
        </div>
      )}
      {chunkProps?.context && (
        <div className="u-els-margin-top">
          <h4 className="u-els-margin-bottom-1o2">Description</h4>
          {chunkProps.context}
        </div>
      )}

      <div className="o-els-container">
        {renderSource()}
      </div>
    </div>
  );
};

const RelatedContentImagePanel = ELSWithToastService(RelatedContentImagePanelComponent);
export default RelatedContentImagePanel;
